import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { useAuthStore } from '@/lib/stores/auth.store';
import { captureBrowserFingerprint, fingerprintToHeaders } from '@/lib/auth/fingerprint';
import { env } from '@/lib/env';
import { getApiErrorMessage } from '@/lib/utils/api-errors';
import type { LoginCredentials } from '../types/auth.types';

// Use the API URL directly
const API_URL = env.VITE_API_URL;

export function useLogin() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((state) => state.setAuth);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const fingerprint = captureBrowserFingerprint();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...fingerprintToHeaders(fingerprint),
      };

      const response = await axios.post(`${API_URL}/auth/seller/login`, {
        email: credentials.email,
        password: credentials.password,
      }, { headers });

      const data = response.data?.data;

      if (!data?.access_token) {
        setError('auth.invalidCredentials');
        return { success: false };
      }

      const expiresIn = data.expires_in || 3600;
      const accessTokenExpires = Date.now() + expiresIn * 1000;

      setAuth({
        user: {
          id: String(data.seller?.id || data.id),
          email: data.seller?.email || data.email,
          firstName: data.seller?.firstName || data.firstName,
          lastName: data.seller?.lastName || data.lastName,
          businessName: data.seller?.businessName || data.businessName,
          role: data.seller?.role || data.role,
          permissions: data.seller?.permissions || data.permissions || [],
        },
        accessToken: data.access_token || data.accessToken,
        refreshToken: data.refresh_token || data.refreshToken,
        accessTokenExpires,
        fingerprint,
      });

      navigate('/');
      return { success: true };
    } catch (err) {
      console.error('[Login] Error:', err);

      if (axios.isAxiosError(err)) {
        const axiosError = err as AxiosError<{ message?: string }>;

        // Handle 429 Too Many Requests (Account Lockout)
        if (axiosError.response?.status === 429) {
          const retryAfter = axiosError.response.headers['retry-after'];
          if (retryAfter) {
            const lockoutTime = new Date(Date.now() + parseInt(retryAfter, 10) * 1000);
            setLockoutUntil(lockoutTime);
          }
          setError(getApiErrorMessage(err));
          return { success: false, isLocked: true };
        }

        // Handle other errors with translated message
        setError(getApiErrorMessage(err));
        return { success: false };
      }

      setError(getApiErrorMessage(err));
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    isLoading,
    error,
    lockoutUntil,
    clearError: () => setError(null),
    clearLockout: () => setLockoutUntil(null),
    isLocked: lockoutUntil ? new Date() < lockoutUntil : false,
  };
}
